import * as React from 'react';
import type { UniqueIdentifier } from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { antGrey } from '@/components/styles/globalStyles';

export interface DragIndexState {
    active: UniqueIdentifier;
    over: UniqueIdentifier | undefined;
    direction?: 'left' | 'right';
    dragType?: 'column' | 'row';
}

export const DragIndexContext = React.createContext<DragIndexState>({ active: -1, over: -1 });

const dragActiveStyle = (dragState: DragIndexState, id: string, axis: 'column' | 'row'): React.CSSProperties => {
    const { active, over, dragType } = dragState;
    if (dragType !== axis) return {};
    if (active && active === id) {
        return { backgroundColor: antGrey.grey4 };
    }
    if (over && id === over && active !== over) {
        return { borderInlineStart: `1px dashed ${antGrey.grey6}` };
    }
    return {};
};

interface HeaderCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
    id: string;
}

interface BodyCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
    id: string;
}

export const TableHeaderCell: React.FC<HeaderCellProps> = (props) => {
    const dragState = React.useContext(DragIndexContext);
    const isDraggable = !!props.id;
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({
        id: props.id ?? '',
        disabled: !isDraggable,
        data: { type: 'column' },
    });

    if (!isDraggable) {
        return <th {...props} />;
    }

    const style: React.CSSProperties = {
        ...props.style,
        cursor: 'move',
        ...(isDragging ? { position: 'relative', zIndex: 9999, userSelect: 'none' } : {}),
        ...dragActiveStyle(dragState, props.id, 'column'),
    };
    return <th {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
};

export const TableBodyCell: React.FC<BodyCellProps> = (props) => {
    const dragState = React.useContext<DragIndexState>(DragIndexContext);
    return <td {...props} style={{ ...props.style, ...dragActiveStyle(dragState, props.id, 'column') }} />;
};

interface BodyRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
    'data-row-key': string;
}

export const TableBodyRow: React.FC<BodyRowProps> = (props) => {
    const dragState = React.useContext(DragIndexContext);
    const rowKey = props['data-row-key'];
    const isDraggable = !!rowKey;
    const { attributes, listeners, setNodeRef, isDragging } = useSortable({
        id: rowKey ?? '',
        disabled: !isDraggable,
        data: { type: 'row' },
    });

    if (!isDraggable) {
        return <tr {...props} />;
    }

    const style: React.CSSProperties = {
        ...props.style,
        cursor: 'move',
        ...(isDragging ? { position: 'relative', zIndex: 9999, userSelect: 'none' } : {}),
        ...dragActiveStyle(dragState, rowKey, 'row'),
    };
    return <tr {...props} ref={setNodeRef} style={style} {...attributes} {...listeners} />;
};
